import { useContext, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { StoresContext } from '../../provider/StoreProvider'

import { useNavigate } from '../../hooks/useNavigate'
import useParamId from '../../hooks/useParamId'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, ColumnItem, RowBetween } from '../../components/common'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Header } from '../../components/header'
import { DetailContent } from '../../components/pagingList'
import { Spinner } from '../../components/spinner/Spinner'
import { ProductInfo } from './components/ProductInfo'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowRightGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray_18.svg'

export const ProductDetail = observer(() => {
  const {
    commonStore: { setInvalidAccess },
    authStore: { userInfo },
    myStore: { purchaseDetailStore, purchasePagingStore },
  } = useContext(StoresContext)

  const navigate = useNavigate()
  const paramId = useParamId()

  const loadParam = { userProductId: paramId }

  useEffect(() => {
    if (typeof paramId === 'undefined') return

    loadData()
    return () => {
      purchaseDetailStore.clearAll()
    }
  }, [paramId])

  useEffect(() => {
    if (!purchaseDetailStore.content || !userInfo) return

    if (purchaseDetailStore.content.user.id !== userInfo?.id) {
      setInvalidAccess(true)
    }
  }, [purchaseDetailStore, userInfo])

  const loadData = async () => {
    try {
      await purchaseDetailStore.load({ param: loadParam }, true)
      await purchasePagingStore.load({ param: loadParam }, true)
    } catch (error) {
      setInvalidAccess(true)
    }
  }

  if (!purchaseDetailStore.content || !purchaseDetailStore.content.product) {
    return (
      <Wrap>
        <Header type="SUB" title="자산 상세" />
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </Wrap>
    )
  }

  const {
    id,
    status,
    product: { id: productId, imageUri, artist, name },
  } = purchaseDetailStore.content

  return (
    <Wrap>
      <Header type="SUB" title="자산 상세" />
      <Content>
        <DetailContent detailStore={purchaseDetailStore}>
          <ProductInfoWrap>
            <ProductInfoCard>
              <ProductInfoContent $gap={16}>
                <ProductImage>
                  <img src={imageUri} alt={name} />
                </ProductImage>
                <ColumnItem $gap={4}>
                  <Text size={18} weight={600}>
                    {artist}
                  </Text>
                  <Text size={14} color="text_secondary">
                    {name}
                  </Text>
                </ColumnItem>
                <ProductDetailLink>
                  <Text
                    size={14}
                    weight={500}
                    color="text_secondary"
                    onClick={() => navigate(`/market/product/${productId}`)}
                  >
                    작품 상세 보기
                  </Text>
                  <ArrowRightGrayIcon />
                </ProductDetailLink>
              </ProductInfoContent>
              <DivideLine />
              {/* TODO: 타입 정리 필요 */}
              <ProductInfo id={id} type={status as string} />
            </ProductInfoCard>
          </ProductInfoWrap>
        </DetailContent>
        <HistoryContainer>
          <Text size={18} weight={600} block={true}>
            투자 내역
          </Text>
          <DetailContent detailStore={purchasePagingStore}>
            <ReverseContainer>
              {purchasePagingStore.content?.map(({ id, createdDate, count, purchaseTypeLabel, amount }) => {
                const YMD = dayjs(new Date(createdDate)).format('YY.MM.DD')
                const HM = dayjs(new Date(createdDate)).format('HH:mm')

                return (
                  <BreakdownContainer key={id}>
                    <TransactionContainer>
                      <ColumnItem $gap={6}>
                        <Text size={10} color="text_secondary">
                          {YMD}
                        </Text>
                        <Text size={10} color="text_secondary">
                          {HM}
                        </Text>
                      </ColumnItem>
                      <ColumnItem $gap={4}>
                        <RowBetween>
                          <Text size={16} weight={500}>
                            {purchaseTypeLabel}
                          </Text>
                          <Text size={18} weight={600}>
                            {Math.abs(count).toLocaleString() ?? 0}주
                          </Text>
                        </RowBetween>
                        <Text size={10} color="text_secondary" $textAlign="right">
                          {Math.abs(amount ?? 0).toLocaleString() ?? 0} 원
                        </Text>
                      </ColumnItem>
                    </TransactionContainer>
                  </BreakdownContainer>
                )
              })}
            </ReverseContainer>
          </DetailContent>
        </HistoryContainer>
      </Content>
    </Wrap>
  )
})

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const ProductInfoWrap = styled.div`
  padding: 16px;
  background-color: ${Color.gray_1};
`

const ProductInfoContent = styled(AlignCenterItem)`
  position: relative;
`

const ProductImage = styled.div`
  overflow: hidden;
  width: 100px;
  aspect-ratio: 1/1.3;
  border-radius: 8px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductDetailLink = styled(AlignCenterItem)`
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  outline: none;
  cursor: pointer;
`

const HistoryContainer = styled.div`
  padding: 24px;
`

const ReverseContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
`

const BreakdownContainer = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid ${Color.line};
`

const TransactionContainer = styled(RowBetween)`
  gap: 32px;
  > * {
    &:first-child {
      flex-shrink: 0;
    }
    &:last-child {
      width: 100%;
    }
  }
`
